import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatPhoneDigitsForAuthLookup, sanitizeLocalMobileInput, validateLocalMobile, getLocalMobileLength, formatPhoneForDisplay } from '../../utils/phone';
import PasswordField from '../../components/PasswordField/PasswordField';
import { EMAIL_RE, readStoredAuth } from './profileFormUtils';
import {
  sendForgotPasswordOtpPhone,
  sendForgotPasswordOtpEmail,
  forgotPasswordSendErrorMessage,
  postForgotPasswordReset,
  forgotPasswordResetErrorMessage,
  validateNewPasswordForApi,
} from './authRecoveryApi';
import './AccountLayout.css';

const COUNTRY_CODES = ['91', '1', '44', '61', '65', '971'];
const OTP_VALID_SEC = 6 * 60;
const OTP_RESEND_COOLDOWN_SEC = 30;

const formatCountdown = (totalSec) => {
  const s = Math.max(0, totalSec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const DOC_TITLE_SUFFIX = 'Digi Rasa';

const AccountForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromResetMenu = searchParams.get('from') === 'reset';

  const [channel, setChannel] = useState('phone');
  const [countryCode, setCountryCode] = useState('91');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');

  const [step, setStep] = useState('request');
  const [validationID, setValidationID] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);

  const { pageTitle, pageSubtitle } = useMemo(() => {
    if (step === 'reset') {
      return {
        pageTitle: 'Reset password',
        pageSubtitle:
          'Enter the 6-digit code we sent, then choose a new password. The code expires after a few minutes.',
      };
    }
    if (fromResetMenu) {
      return {
        pageTitle: 'Reset password',
        pageSubtitle:
          'Enter the mobile number on your account. We will send a one-time code so you can set a new password.',
      };
    }
    return {
      pageTitle: 'Forgot password',
      pageSubtitle:
        'We will send a one-time code to your mobile number. Then you can set a new password.',
    };
  }, [step, fromResetMenu]);

  useEffect(() => {
    const prev = document.title;
    const base =
      step === 'reset'
        ? 'Reset password'
        : fromResetMenu
          ? 'Reset password'
          : 'Forgot password';
    document.title = `${base} · ${DOC_TITLE_SUFFIX}`;
    return () => {
      document.title = prev;
    };
  }, [step, fromResetMenu]);

  useEffect(() => {
    if (step !== 'reset') return undefined;
    const id = window.setInterval(() => {
      setOtpSecondsLeft((t) => (t > 0 ? t - 1 : 0));
      setResendCooldownSec((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step]);

  const sendOtp = useCallback(async () => {
    setError('');
    setSuccess('');

    if (channel === 'phone') {
      const phoneCheck = validateLocalMobile(mobile, countryCode);
      if (!phoneCheck.ok) {
        setError(phoneCheck.message);
        return;
      }
      const phoneId = formatPhoneDigitsForAuthLookup(countryCode, phoneCheck.digits);
      setLoading(true);
      try {
        const { res, data } = await sendForgotPasswordOtpPhone(phoneId);
        if (!res.ok) {
          throw new Error(forgotPasswordSendErrorMessage(res.status, data, 'phone'));
        }
        setValidationID(phoneId);
        setStep('reset');
        setOtp('');
        setPassword('');
        setPassword2('');
        setOtpSecondsLeft(OTP_VALID_SEC);
        setResendCooldownSec(OTP_RESEND_COOLDOWN_SEC);
        setSuccess(data.message || 'OTP sent successfully.');
      } catch (e) {
        setError(e.message || 'Failed to send OTP.');
      } finally {
        setLoading(false);
      }
      return;
    }

    const em = String(email).trim();
    if (!EMAIL_RE.test(em)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const { res, data } = await sendForgotPasswordOtpEmail(em);
      if (!res.ok) {
        throw new Error(forgotPasswordSendErrorMessage(res.status, data, 'email'));
      }
      setValidationID(em);
      setStep('reset');
      setOtp('');
      setPassword('');
      setPassword2('');
      setOtpSecondsLeft(OTP_VALID_SEC);
      setResendCooldownSec(OTP_RESEND_COOLDOWN_SEC);
      setSuccess(data.message || 'OTP sent successfully.');
    } catch (e) {
      setError(e.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  }, [channel, countryCode, mobile, email]);

  const handleResend = async () => {
    if (resendCooldownSec > 0 || loading || !validationID) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (channel === 'phone') {
        const { res, data } = await sendForgotPasswordOtpPhone(validationID);
        if (!res.ok) throw new Error(forgotPasswordSendErrorMessage(res.status, data, 'phone'));
      } else {
        const { res, data } = await sendForgotPasswordOtpEmail(validationID);
        if (!res.ok) throw new Error(forgotPasswordSendErrorMessage(res.status, data, 'email'));
      }
      setOtpSecondsLeft(OTP_VALID_SEC);
      setResendCooldownSec(OTP_RESEND_COOLDOWN_SEC);
      setSuccess('OTP sent again.');
    } catch (e) {
      setError(e.message || 'Could not resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const otpDigits = otp.replace(/\D/g, '');
    if (otpDigits.length !== 6) {
      setError('Enter the 6-digit OTP.');
      return;
    }
    const pwMsg = validateNewPasswordForApi(password);
    if (pwMsg) {
      setError(pwMsg);
      return;
    }
    if (password !== password2) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { res, data } = await postForgotPasswordReset({
        validationID,
        otp: otpDigits,
        password,
      });
      if (!res.ok) {
        throw new Error(forgotPasswordResetErrorMessage(res.status, data));
      }
      let successMsg = 'Your password was reset successfully. Please sign in with your new password.';
      if (data && typeof data.message === 'string' && data.message.trim()) {
        successMsg = data.message.trim();
      }
      const wasSignedIn = Boolean(readStoredAuth().token);
      try {
        sessionStorage.setItem(
          'digirasa_password_updated',
          JSON.stringify({
            title: 'Password reset',
            message: successMsg,
            signedOut: wasSignedIn,
          }),
        );
      } catch {
        /* ignore */
      }
      navigate('/account/password-updated', {
        replace: true,
        state: {
          title: 'Password reset',
          message: successMsg,
          signedOut: wasSignedIn,
        },
      });
    } catch (e) {
      setError(e.message || 'Reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const backToRequest = () => {
    setStep('request');
    setError('');
    setSuccess('');
    setOtp('');
    setPassword('');
    setPassword2('');
    setValidationID('');
    setOtpSecondsLeft(0);
    setResendCooldownSec(0);
  };

  return (
    <div className="account-page account-page--recovery">
      <div className="account-page__title-block">
        <h1>{pageTitle}</h1>
        {pageSubtitle ? <p className="account-page__muted">{pageSubtitle}</p> : null}
      </div>

      {error ? (
        <div
          className="auth-inline-message auth-inline-message--error"
          style={{ marginBottom: '12px' }}
        >
          {error}
        </div>
      ) : null}
      {success && step === 'reset' ? (
        <div
          className="auth-inline-message auth-inline-message--success"
          style={{ marginBottom: '12px' }}
        >
          {success}
        </div>
      ) : null}

      {step === 'request' ? (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            sendOtp();
          }}
          noValidate
        >
          {/* Email recovery is hidden for now — channel stays 'phone' by default. */}
          <div className="auth-form__group">
            <label htmlFor="fp-mobile">Mobile number</label>
            <div className="auth-phone-row">
              <select
                id="fp-cc"
                value={countryCode}
                onChange={(ev) => {
                  const nextCc = ev.target.value;
                  setCountryCode(nextCc);
                  setMobile((prev) => sanitizeLocalMobileInput(prev, nextCc));
                }}
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((code) => (
                  <option key={code} value={code}>
                    +{code}
                  </option>
                ))}
              </select>
              <input
                id="fp-mobile"
                type="tel"
                inputMode="numeric"
                value={mobile}
                onChange={(ev) => setMobile(sanitizeLocalMobileInput(ev.target.value, countryCode))}
                placeholder={
                  countryCode === '91' ? 'e.g. 9876543210' : `${getLocalMobileLength(countryCode)} digits`
                }
                maxLength={getLocalMobileLength(countryCode)}
                autoComplete="tel"
              />
            </div>
            <p className="auth-form__hint">
              Use the same number you use to log in (country code above + local digits only, no +
              or spaces). If you paste a full number including 91, we won’t add the country code
              twice.
            </p>
          </div>

          <button type="submit" className="auth-form__submit" disabled={loading}>
            {loading ? 'Sending…' : 'Send OTP'}
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={handleResetSubmit} noValidate>
          <p className="auth-form__hint">
            Code sent to{' '}
            {channel === 'phone' ? formatPhoneForDisplay(validationID) : validationID}. Valid for{' '}
            <strong>{formatCountdown(otpSecondsLeft)}</strong>
            {otpSecondsLeft === 0 ? ' — request a new code below.' : ''}
          </p>

          <div className="auth-form__group">
            <label htmlFor="fp-otp">6-digit OTP</label>
            <input
              id="fp-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(ev) => setOtp(ev.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
            />
          </div>
          <div className="auth-form__group">
            <label htmlFor="fp-pw">New password</label>
            <PasswordField
              id="fp-pw"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              placeholder="8+ chars, letters, numbers, special character"
              autoComplete="new-password"
              disabled={loading}
            />
            <p className="auth-form__hint">
              No spaces. Include letters, numbers, and a special character.
            </p>
          </div>
          <div className="auth-form__group">
            <label htmlFor="fp-pw2">Confirm new password</label>
            <PasswordField
              id="fp-pw2"
              value={password2}
              onChange={(ev) => setPassword2(ev.target.value)}
              autoComplete="new-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="auth-form__submit"
            disabled={loading || otpSecondsLeft === 0}
          >
            {loading ? 'Saving…' : 'Reset password'}
          </button>

          <div className="auth-form__row-actions" style={{ marginTop: '12px' }}>
            <button
              type="button"
              className="auth-form__secondary-btn"
              disabled={resendCooldownSec > 0 || loading}
              onClick={handleResend}
            >
              {resendCooldownSec > 0 ? `Resend in ${resendCooldownSec}s` : 'Resend OTP'}
            </button>
            <button type="button" className="auth-form__text-btn" onClick={backToRequest}>
              Change mobile number
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AccountForgotPassword;
